import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  getStorage,
} from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { db } from '../config/firebase';
import { Product, ProductFormData, ProductFilters, ProductStats, LoyaltyLevel, ProductDownload } from '../types/productTypes';

const COLLECTION_NAME = 'products';
const DOWNLOADS_COLLECTION = 'productDownloads';
const STORAGE_PATH = 'products';

class ProductService {
  private storage = getStorage();

  /**
   * Get all products with optional filtering
   */
  async getProducts(filters?: ProductFilters): Promise<Product[]> {
    try {
      let q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));

      // Apply filters
      if (filters?.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }

      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[];

      // Apply client-side filters
      let filteredProducts = products;

      if (filters?.loyaltyLevel) {
        filteredProducts = filteredProducts.filter(product =>
          product.loyaltyLevels.includes(filters.loyaltyLevel!)
        );
      }

      if (filters?.searchQuery) {
        const searchTerm = filters.searchQuery.toLowerCase();
        filteredProducts = filteredProducts.filter(product =>
          product.name.toLowerCase().includes(searchTerm) ||
          product.description.toLowerCase().includes(searchTerm)
        );
      }

      return filteredProducts;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Get a single product by ID
   */
  async getProduct(id: string): Promise<Product | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Product;
      }

      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  /**
   * Compress image if it's an image file
   */
  private async compressImageIfNeeded(file: File): Promise<File> {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      return file;
    }

    try {
      const options = {
        maxSizeMB: 1, // Maximum file size in MB
        maxWidthOrHeight: 1200, // Maximum width or height
        useWebWorker: true,
        fileType: file.type.includes('png') ? 'image/png' : 'image/jpeg',
        quality: 0.8, // Image quality (0.1 - 1.0)
      };

      const compressedFile = await imageCompression(file, options);
      
      // Create a new file with the original name but compressed content
      return new File([compressedFile], file.name, {
        type: compressedFile.type,
        lastModified: Date.now(),
      });
    } catch (error) {
      console.warn('Image compression failed, using original file:', error);
      return file;
    }
  }

  /**
   * Upload file to Firebase Storage with automatic image compression
   */
  private async uploadFile(file: File, path: string): Promise<{ url: string; storagePath: string }> {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storagePath = `${STORAGE_PATH}/${path}/${fileName}`;
    const storageRef = ref(this.storage, storagePath);

    // Compress image if it's an image file
    const fileToUpload = await this.compressImageIfNeeded(file);
    
    await uploadBytes(storageRef, fileToUpload);
    const url = await getDownloadURL(storageRef);

    return { url, storagePath };
  }

  /**
   * Delete file from Firebase Storage
   */
  private async deleteFile(storagePath: string): Promise<void> {
    try {
      const storageRef = ref(this.storage, storagePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      // Don't throw error as the document might still be valid
    }
  }

  /**
   * Create a new product
   */
  async createProduct(productData: ProductFormData, userId: string): Promise<string> {
    try {
      const productDoc: Partial<Product> = {
        name: productData.name,
        description: productData.description,
        loyaltyLevels: productData.loyaltyLevels,
        downloadCount: 0,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      };

      // Upload image if provided
      if (productData.imageFile) {
        const { url, storagePath } = await this.uploadFile(productData.imageFile, 'images');
        productDoc.imageUrl = url;
        productDoc.imageStoragePath = storagePath;
      }

      // Upload download file if provided
      if (productData.downloadFile) {
        const { url, storagePath } = await this.uploadFile(productData.downloadFile, 'downloads');
        productDoc.downloadUrl = url;
        productDoc.downloadStoragePath = storagePath;
        productDoc.fileSize = productData.downloadFile.size;
        productDoc.fileType = productData.downloadFile.type;
      }

      const docRef = await addDoc(collection(db, COLLECTION_NAME), productDoc);
      return docRef.id;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  /**
   * Update an existing product
   */
  async updateProduct(id: string, productData: ProductFormData, userId: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const currentProduct = await this.getProduct(id);

      if (!currentProduct) {
        throw new Error('Product not found');
      }

      const updateData: Partial<Product> = {
        name: productData.name,
        description: productData.description,
        loyaltyLevels: productData.loyaltyLevels,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      };

      // Handle image update
      if (productData.imageFile) {
        // Delete old image if exists
        if (currentProduct.imageStoragePath) {
          await this.deleteFile(currentProduct.imageStoragePath);
        }

        // Upload new image
        const { url, storagePath } = await this.uploadFile(productData.imageFile, 'images');
        updateData.imageUrl = url;
        updateData.imageStoragePath = storagePath;
      }

      // Handle download file update
      if (productData.downloadFile) {
        // Delete old file if exists
        if (currentProduct.downloadStoragePath) {
          await this.deleteFile(currentProduct.downloadStoragePath);
        }

        // Upload new file
        const { url, storagePath } = await this.uploadFile(productData.downloadFile, 'downloads');
        updateData.downloadUrl = url;
        updateData.downloadStoragePath = storagePath;
        updateData.fileSize = productData.downloadFile.size;
        updateData.fileType = productData.downloadFile.type;
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  /**
   * Delete a product
   */
  async deleteProduct(id: string): Promise<void> {
    try {
      const product = await this.getProduct(id);

      if (!product) {
        throw new Error('Product not found');
      }

      // Delete associated files
      if (product.imageStoragePath) {
        await this.deleteFile(product.imageStoragePath);
      }

      if (product.downloadStoragePath) {
        await this.deleteFile(product.downloadStoragePath);
      }

      // Delete document
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  /**
   * Toggle product active status
   */
  async toggleProductStatus(id: string, userId: string): Promise<void> {
    try {
      const product = await this.getProduct(id);

      if (!product) {
        throw new Error('Product not found');
      }

      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        isActive: !product.isActive,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      });
    } catch (error) {
      console.error('Error toggling product status:', error);
      throw error;
    }
  }

  /**
   * Increment download count
   */
  async incrementDownloadCount(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        downloadCount: increment(1),
      });
    } catch (error) {
      console.error('Error incrementing download count:', error);
      throw error;
    }
  }

  /**
   * Get products statistics
   */
  async getProductStats(): Promise<ProductStats> {
    try {
      const products = await this.getProducts();
      const downloads = await this.getAllDownloads();

      const uniqueDownloaders = new Set(downloads.map((d: ProductDownload) => d.userId)).size;

      const stats: ProductStats = {
        totalProducts: products.length,
        activeProducts: products.filter(p => p.isActive).length,
        inactiveProducts: products.filter(p => !p.isActive).length,
        totalDownloads: products.reduce((sum, p) => sum + p.downloadCount, 0),
        productsByLevel: {
          bronze: products.filter(p => p.loyaltyLevels.includes('bronze')).length,
          silver: products.filter(p => p.loyaltyLevels.includes('silver')).length,
          gold: products.filter(p => p.loyaltyLevels.includes('gold')).length,
        },
        downloadsByLevel: {
          bronze: downloads.filter((d: ProductDownload) => d.userLevel === 'bronze').length,
          silver: downloads.filter((d: ProductDownload) => d.userLevel === 'silver').length,
          gold: downloads.filter((d: ProductDownload) => d.userLevel === 'gold').length,
        },
        uniqueDownloaders,
      };

      return stats;
    } catch (error) {
      console.error('Error fetching product stats:', error);
      throw error;
    }
  }

  /**
   * Get products available for a specific loyalty level
   */
  async getProductsForLevel(userLevel: LoyaltyLevel): Promise<Product[]> {
    try {
      const products = await this.getProducts({ isActive: true });
      return products.filter(product => product.loyaltyLevels.includes(userLevel));
    } catch (error) {
      console.error('Error fetching products for level:', error);
      throw error;
    }
  }

  /**
   * Check if user can access a product based on their loyalty level
   */
  canUserAccessProduct(product: Product, userLevel: LoyaltyLevel): boolean {
    return product.isActive && product.loyaltyLevels.includes(userLevel);
  }

  /**
   * Record a product download
   */
  async recordDownload(
    productId: string,
    productName: string,
    userId: string,
    userEmail: string,
    userDisplayName: string,
    userLevel: LoyaltyLevel
  ): Promise<void> {
    try {
      const downloadRecord: Partial<ProductDownload> = {
        productId,
        productName,
        userId,
        userEmail,
        userDisplayName,
        userLevel,
        downloadedAt: serverTimestamp(),
      };

      // Add download record
      await addDoc(collection(db, DOWNLOADS_COLLECTION), downloadRecord);

      // Increment download count on product
      await this.incrementDownloadCount(productId);
    } catch (error) {
      console.error('Error recording download:', error);
      throw error;
    }
  }

  /**
   * Get all download records (admin only)
   */
  async getAllDownloads(): Promise<ProductDownload[]> {
    try {
      const q = query(collection(db, DOWNLOADS_COLLECTION), orderBy('downloadedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ProductDownload[];
    } catch (error) {
      console.error('Error fetching downloads:', error);
      return [];
    }
  }

  /**
   * Get downloads for a specific product
   */
  async getProductDownloads(productId: string): Promise<ProductDownload[]> {
    try {
      const q = query(
        collection(db, DOWNLOADS_COLLECTION),
        where('productId', '==', productId),
        orderBy('downloadedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ProductDownload[];
    } catch (error) {
      console.error('Error fetching product downloads:', error);
      return [];
    }
  }

  /**
   * Get downloads for a specific user
   */
  async getUserDownloads(userId: string): Promise<ProductDownload[]> {
    try {
      const q = query(
        collection(db, DOWNLOADS_COLLECTION),
        where('userId', '==', userId),
        orderBy('downloadedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ProductDownload[];
    } catch (error) {
      console.error('Error fetching user downloads:', error);
      return [];
    }
  }

  /**
   * Check if user has already downloaded a product
   */
  async hasUserDownloaded(productId: string, userId: string): Promise<boolean> {
    try {
      const q = query(
        collection(db, DOWNLOADS_COLLECTION),
        where('productId', '==', productId),
        where('userId', '==', userId),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking download history:', error);
      return false;
    }
  }

  /**
   * Get download analytics
   */
  async getDownloadAnalytics(days: number = 30): Promise<{
    totalDownloads: number;
    uniqueUsers: number;
    downloadsByDay: { date: string; count: number }[];
    topProducts: { productName: string; downloads: number }[];
    downloadsByLevel: { level: LoyaltyLevel; count: number }[];
  }> {
    try {
      const downloads = await this.getAllDownloads();
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      // Filter downloads within the specified period
      const recentDownloads = downloads.filter(download => {
        const downloadDate = download.downloadedAt?.toDate ? 
          download.downloadedAt.toDate() : 
          new Date(download.downloadedAt);
        return downloadDate >= cutoffDate;
      });

      // Calculate analytics
      const uniqueUsers = new Set(recentDownloads.map(d => d.userId)).size;

      // Downloads by day
      const downloadsByDay = Array.from({ length: days }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (days - 1 - i));
        const dateStr = date.toISOString().split('T')[0];
        
        const count = recentDownloads.filter(download => {
          const downloadDate = download.downloadedAt?.toDate ? 
            download.downloadedAt.toDate() : 
            new Date(download.downloadedAt);
          return downloadDate.toISOString().split('T')[0] === dateStr;
        }).length;

        return { date: dateStr, count };
      });

      // Top products
      const productCounts = recentDownloads.reduce((acc, download) => {
        acc[download.productName] = (acc[download.productName] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const topProducts = Object.entries(productCounts)
        .map(([productName, downloads]) => ({ productName, downloads }))
        .sort((a, b) => b.downloads - a.downloads)
        .slice(0, 10);

      // Downloads by level
      const levelCounts = recentDownloads.reduce((acc, download) => {
        acc[download.userLevel] = (acc[download.userLevel] || 0) + 1;
        return acc;
      }, {} as Record<LoyaltyLevel, number>);

      const downloadsByLevel = (['bronze', 'silver', 'gold'] as LoyaltyLevel[])
        .map(level => ({ level, count: levelCounts[level] || 0 }));

      return {
        totalDownloads: recentDownloads.length,
        uniqueUsers,
        downloadsByDay,
        topProducts,
        downloadsByLevel,
      };
    } catch (error) {
      console.error('Error fetching download analytics:', error);
      return {
        totalDownloads: 0,
        uniqueUsers: 0,
        downloadsByDay: [],
        topProducts: [],
        downloadsByLevel: [],
      };
    }
  }
}

export const productService = new ProductService();
export default productService; 